import { DoubleLeftOutlined, EllipsisOutlined, HomeOutlined, PlusOutlined, RestOutlined } from '@ant-design/icons';
import { Avatar, Button, Menu, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getColorByName } from '../../utils/common';
import { useFileNavigationContext } from '../../contexts/FileNavigationContext';
import useChangeNavigationOnActiveFile from '../../hooks/useChangeNavigationOnActiveFile';
import PopoverMenu from '../PopoverMenu/PopoverMenu';
import useDocumentCreation from '../../hooks/useDocumentCreation';
import { useNavigate, useParams } from 'react-router-dom';
import useFileOperationsHandler from '../Leftbar/FileOperationsHandler';
import { useFileOperationsContext } from '../../contexts/FileOperationsContext';
import { HIDE_GIT_BASED_FILES, HIDE_GIT_BASED_FOLDERS } from '../UserSpace/constant';
import FileExplorer from '@components/FileExplorerMenu';
import { TooltipButton } from '../TooltipButton';
import { fetchAllFolders, setActiveWorkspaceAction } from '../../features/organization/organizationSlice';
const { Text } = Typography;

const index = ({ popoverMenuContent }) => {
  const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
  const fetchAllFilesLoading = useSelector((state) => state.organization?.loading?.fetchAllFiles);
  const collapseLeftBar = useSelector((state) => state.organization?.collapseLeftBar);
  const userPermissions = useSelector((state) => state.user?.userPermissions);
  const workspaceList = useSelector((state) => state.organization?.workspaceList);
  const starredWorkspace = useSelector((state) => state.organization?.starredWorkspace?.data);
  const { changeNavigationOnActiveFile } = useChangeNavigationOnActiveFile();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { createDocument } = useDocumentCreation();
  const { currentSelected, setCurrentSelected, expandedKeys, setExpandedKeys } = useFileNavigationContext();

  const workspaceFoldersList = useSelector((state) => state.organization?.workspaceFoldersList);
  const { openApiClickHandler, onMenuClickHandler } = useFileOperationsHandler(changeNavigationOnActiveFile);

  // Get file operations from context
  const { getFolderOptionContent, getFileOptionContent } = useFileOperationsContext();

  // Fetch folders when workspace ID changes
  useEffect(() => {
    if (workspaceList?.length > 0 && params?.spaceId) {
      const workspace = workspaceList?.find((item) => item?.slug === params?.spaceId);
      if (workspace?.id) {
        if (activeWorkspace?.id != workspace?.id) dispatch(setActiveWorkspaceAction(params?.spaceId));
        dispatch(fetchAllFolders(workspace?.id));
      }
    }
  }, [params?.spaceId, workspaceList?.length]);

  // Create workspace menu items for the header
  const items = useMemo(
    () => [
      {
        key: 'home',
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>Back to home</Text>
            <DoubleLeftOutlined />
          </div>
        ),
        icon: <HomeOutlined />,
        onClick: () => navigate('/home'),
      },
    ],
    [navigate]
  );

  const workspaceHeader = useMemo(() => {
    const isStarred = starredWorkspace?.find((item) => item?.id === activeWorkspace?.id);
    return [
      {
        key: activeWorkspace?.slug,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text
              ellipsis={{ tooltip: true }}
              style={{
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'inherit',
              }}
            >
              {activeWorkspace?.title}
            </Text>
            <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
              <PlusOutlined onClick={() => createDocument()} style={{ margin: 0 }} />
              <PopoverMenu
                id={activeWorkspace?.id}
                content={popoverMenuContent(isStarred)}
                arrow={false}
                scroll="true"
                placement="right"
              >
                <EllipsisOutlined style={{ margin: 0 }} />
              </PopoverMenu>
            </div>
          </div>
        ),
        icon: (
          <Avatar
            size={24}
            style={{
              backgroundColor: getColorByName(activeWorkspace?.title),
              borderRadius: '6px',
              color: '#fff',
            }}
          >
            {activeWorkspace?.title?.slice(0, 1)}
          </Avatar>
        ),
        className: 'workspace-menu-selected',
        onClick: (e) => {
          changeNavigationOnActiveFile({ filePath: 'workspace', workspaceId: e.key });
          setCurrentSelected('');
        },
        popupClassName: 'workspace-submenu-popup',
        popupOffset: [0, 0],
      },
    ];
  }, [
    activeWorkspace,
    createDocument,
    popoverMenuContent,
    changeNavigationOnActiveFile,
    setCurrentSelected,
    starredWorkspace,
  ]);

  const folderOptionContent = useMemo(() => {
    const options = getFolderOptionContent();
    return options.map((option) => ({
      ...option,
      element: (
        <Button type="button" htmlType="button">
          {option.element}
        </Button>
      ),
    }));
  }, [getFolderOptionContent]);

  // Memoized file options
  const fileOptionContent = useMemo(() => {
    const options = getFileOptionContent();
    return options.map((option) => ({
      ...option,
      element: (
        <Button type="button" htmlType="button">
          {option.element}
        </Button>
      ),
    }));
  }, [getFileOptionContent]);

  const navigateToTrash = useCallback(() => {
    navigate(`/spaces/${activeWorkspace?.slug}/trash`);
    return;
  }, [activeWorkspace]);

  return (
    <div>
      <Menu mode="inline" items={items} />
      {/* Divider */}
      <div className="divider" style={{ margin: 0 }} />
      <Menu mode="inline" items={workspaceHeader} />
      {!collapseLeftBar && (
        <div className="menu-container visible" style={{ padding: '0 16px' }}>
          <FileExplorer
            isGitBased={activeWorkspace?.is_git_based}
            data={workspaceFoldersList}
            explorerRootId={activeWorkspace?.id}
            onMenuClick={onMenuClickHandler}
            isFolderLoading={fetchAllFilesLoading}
            folderOptions={folderOptionContent}
            fileOptions={fileOptionContent}
            gitFolderOptionsToHide={HIDE_GIT_BASED_FOLDERS}
            gitFileOptionsToHide={HIDE_GIT_BASED_FILES}
            userPermissions={userPermissions}
            isOrganizationAdmin={userPermissions?.is_organization_admin}
            initialOpened={expandedKeys}
            openApiClickHandler={openApiClickHandler}
            currentSelected={currentSelected}
            expandedKeys={expandedKeys}
            setCurrentExpandedKeys={setExpandedKeys}
            setCurrentSelected={setCurrentSelected}
          />
        </div>
      )}
      <div className="workspace-action-menu" style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
        <TooltipButton tooltipText={`${activeWorkspace?.title} trash`} onClick={navigateToTrash}>
          <RestOutlined />
        </TooltipButton>
      </div>
    </div>
  );
};

export default index;
